import { useEffect, useState } from "react";
import { isAbortError } from "@/lib/abort-error";
import { ApiStatusError, getChatSubagentRun } from "@/lib/api";
import { isTerminalSubagentStatus, type SubagentRunDetail } from "@/lib/chat";
import { mergeSubagentTranscript } from "@/lib/subagent-tasks";
import { useChatStore } from "@/stores/useChatStore";

const POLL_INTERVAL_MS = 2_000;

/**
 * 再取得しても結果が変わらない応答 (存在しない run・権限なし・入力不正)。
 * 408/429 は同じ 4xx でも時間をおけば通るので再試行に回す。ステータスを持たないエラー
 * (通信断・JSON 破損) も同様に再試行する。
 */
export function isUnrecoverableFetchError(error: unknown): boolean {
	if (!(error instanceof ApiStatusError)) return false;
	if (error.status === 408 || error.status === 429) return false;
	return error.status >= 400 && error.status < 500;
}

export function useSubagentTranscript(runId: string) {
	const chatId = useChatStore((state) => state.chatId);
	const [detail, setDetail] = useState<SubagentRunDetail | null>(null);
	const [error, setError] = useState(false);
	useEffect(() => {
		if (!chatId) return;
		const controller = new AbortController();
		let timer: ReturnType<typeof setTimeout> | undefined;
		let disposed = false;
		let fromOrder: number | undefined;
		const load = async () => {
			try {
				const next = await getChatSubagentRun(chatId, runId, {
					fromOrder,
					signal: controller.signal,
				});
				if (disposed) return;
				setDetail((previous) => mergeSubagentTranscript(previous, next));
				if (next.messages.length > 0) fromOrder = next.messages.at(-1)!.order;
				setError(false);
				if (!isTerminalSubagentStatus(next.run.status)) timer = setTimeout(load, POLL_INTERVAL_MS);
			} catch (caught) {
				if (disposed || isAbortError(caught)) return;
				console.error("サブエージェント実行履歴の取得に失敗しました", caught);
				setError(true);
				// 回復不能な応答 (4xx) を 2 秒ごとに叩き続けない。終端済みの run はそもそも次の
				// 取得を予約しない (成功時の分岐が担当) ため、ここで見る必要は無い。
				if (!isUnrecoverableFetchError(caught)) timer = setTimeout(load, POLL_INTERVAL_MS);
			}
		};
		void load();
		return () => {
			disposed = true;
			controller.abort();
			if (timer) clearTimeout(timer);
		};
	}, [chatId, runId]);
	return { detail, error };
}
